import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { MatFabButton, MatIconButton } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltip } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe } from '@ngx-translate/core';
import {
  HasPermissionsDirective,
  MenuNavigationService,
  UserProvider,
} from '@siat-fe/auth';
import { GlobalActionsService, NavMenuItem } from '@siat-fe/core';
import { environment } from '@siat-fe/env';
import {
  CustomModalOptions,
  SearchPatientDialog,
  SmartModalService,
} from '@siat-fe/shared';
import { map, tap } from 'rxjs';
import { FooterComponent } from '../footer/footer.component';
import { SidenavMenuComponent } from './sidenav-menu.component';
@UntilDestroy()
@Component({
  imports: [
    CommonModule,
    RouterModule,
    MatSidenavModule,
    MatListModule,
    MatFabButton,
    MatIconModule,
    MatToolbarModule,
    SidenavMenuComponent,
    MatIconButton,
    MatTooltip,
    TranslatePipe,
    FooterComponent,
  ],
  providers: [HasPermissionsDirective],
  selector: 'siat-sidebar',
  templateUrl: './sidebar.component.html',

  styles: [
    `
      .secondary-sidenav {
        width: 200px;
      }
      button:focus {
        outline: 2px solid black;
        outline-offset: 2px;
      }
      .secondary-sidenav {
        .active-sub-item {
          background-color: var(--mat-sys-secondary-container) !important;
          span {
            color: var(--mat-sys-primary) !important;
          }
        }
      }
    `,
  ],
})
export class SidebarComponent {
  /**
   * The application version.
   */
  appVersion = environment.version;
  /**
   * A reference to the secondary sidenav.
   */
  @ViewChild('secondarySidenav')
  secondarySidenav!: MatSidenav;

  @ViewChild('mainContent')
  mainContent!: ElementRef<HTMLElement>;

  /**
   * The service used to navigate between menu items.
   */
  menuNavigationService = inject(MenuNavigationService);

  /**
   * The user provider service.
   */
  userProvider = inject(UserProvider);

  /**
   * The router used to navigate between menu items.
   */
  router = inject(Router);

  /**
   * The global actions service.
   */
  globalActionsService = inject(GlobalActionsService);

  /**
   * It streams the current app menu items.
   */
  currentAppMenuItems$ = this.menuNavigationService.currentAppMenuItems$;

  /**
   * Check if user has authorities to show menu items.
   */
  hasAuthorities$ = this.userProvider.currentUser$.pipe(
    map((user) => user?.authorities && user.authorities.length > 0),
  );

  /**
   * Emits true only when the selected struttura has nomeTipologia === 'Struttura'.
   * Used to hide special-action FABs (e.g. nuova segnalazione) for unità operative.
   */
  isStrutturaType$ = this.userProvider.selectedStruttura$.pipe(
    map((struttura) => struttura?.nomeTipologia === 'Struttura'),
  );

  /**
   * The currently selected menu item.
   */
  selectedMenu: NavMenuItem | null = null;

  /**
   * Emits an event to toggle the sidebar.
   */
  @Output()
  toggleSidebar = new EventEmitter<void>();

  /**
   * Determines if the sidebar is collapsed.
   */
  @Input()
  isCollapsed = true;

  smartDialogService = inject(SmartModalService);

  constructor() {
    this.subscribeToGlobalActions();
  }

  /**
   * Handles item click by emitting an event to the parent component.
   */
  onPrimaryNavItemClicked(item: NavMenuItem): void {
    if (item.isSpecialAction && item.specialActionType) {
      this.globalActionsService.triggerAction({
        type: item.specialActionType,
        payload: item,
      });
      return;
    }

    if (item.subItems && item.subItems.length > 0) {
      if (this.selectedMenu === item && this.secondarySidenav.opened) {
        this.secondarySidenav.close();
        this.selectedMenu = null;
      } else {
        this.selectedMenu = item;
        this.secondarySidenav.open();
      }
    } else {
      this.secondarySidenav.close();
      this.selectedMenu = null;

      if (item.href) {
        this.router.navigate([item.href]);
      } else if (item.url) {
        window.open(item.url, '_blank');
      }
    }
  }

  /**
   * Tracks the item by its title or index.
   */
  trackByFn(index: number, item: NavMenuItem): string | number {
    return item.title || index;
  }

  /**
   * Subscribe to global actions
   */
  private subscribeToGlobalActions(): void {
    this.globalActionsService.action$
      .pipe(
        untilDestroyed(this),
        tap((action) => {
          if (action.type === 'nuova-segnalazione') {
            this.openNuovaSegnalazioneModal();
          }
        }),
      )
      .subscribe();
  }

  /**
   * Open Nuova Segnalazione modal
   */
  openNuovaSegnalazioneModal() {
    const modalOption = {
      config: {
        width: '500px',
        disableClose: true,
        hasBackdrop: true,
      },
    } as CustomModalOptions;

    this.smartDialogService.openModalFromComponent(
      SearchPatientDialog,
      modalOption,
    );
  }

  goToMainContent() {
    this.mainContent.nativeElement.focus();
  }
}
